import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = 'https://j7n0d932k6.execute-api.us-east-1.amazonaws.com/prod';

function App() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLeads();
    const interval = setInterval(fetchLeads, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/leads?agentEmail=stevenaaronnagy@gmail.com`);
      const data = await response.json();
      console.log('Fetched leads:', data.leads);
      setLeads(data.leads || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const viewLead = async (lead) => {
    console.log('Viewing lead:', lead);
    
    // If no leadId, use the lead data we already have
    if (!lead.leadId) {
      setSelectedLead({
        ...lead,
        conversation: [] // No conversation available without leadId
      });
      setShowModal(true);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/leads/${lead.leadId}`);
      const data = await response.json();
      console.log('Lead details:', data);
      setSelectedLead(data.lead);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching lead details:', error);
      alert('Could not load conversation. LeadId may be missing.');
    }
  };

  const updateLeadStatus = async (lead, status) => {
    console.log('Updating status for:', lead, 'to:', status);
    
    if (!lead.leadId) {
      alert('Cannot update status - Lead ID is missing');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/leads/${lead.leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      const result = await response.json();
      console.log('Update result:', result);
      
      if (result.success) {
        // Update local state
        setLeads(leads.map(l => 
          l.leadId === lead.leadId ? { ...l, status } : l
        ));
        
        if (selectedLead?.leadId === lead.leadId) {
          setSelectedLead({ ...selectedLead, status });
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    if (!leadId) {
      alert('Cannot save notes - Lead ID is missing');
      return;
    }
    
    try {
      await fetch(`${API_URL}/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes })
      });
      alert('Notes saved!');
      fetchLeads();
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Failed to save notes');
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Budget', 'Bedrooms', 'Timeline', 'Pre-Approval', 'Temperature', 'Score', 'Status'];
    const rows = filteredAndSortedLeads.map(lead => [
      new Date(lead.createdAt).toLocaleDateString(),
      lead.name || '',
      lead.email || '',
      lead.phone || '',
      lead.budget || '',
      lead.bedrooms || '',
      lead.timeline || '',
      lead.preApproval || '',
      lead.temperature,
      lead.leadScore || 0,
      lead.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const sortLeads = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedLeads = leads
    .filter(lead => {
      const matchesSearch = 
        lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone?.includes(searchTerm);
      
      const matchesTempFilter = 
        filter === 'all' ||
        (filter === 'hot' && lead.temperature === 'HOT') ||
        (filter === 'warm' && lead.temperature === 'WARM') ||
        (filter === 'cold' && lead.temperature === 'COLD');
      
      const matchesStatusFilter = 
        statusFilter === 'all' || lead.status === statusFilter;
      
      return matchesSearch && matchesTempFilter && matchesStatusFilter;
    })
    .sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      
      if (sortConfig.key === 'createdAt') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortConfig.key === 'leadScore') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const getTempColor = (temp) => {
    if (temp === 'HOT') return '#ef4444';
    if (temp === 'WARM') return '#f59e0b';
    return '#6b7280';
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading leads...</p>
      </div>
    );
  }

  const avgScore = Math.round(leads.reduce((sum, l) => sum + (l.leadScore || 0), 0) / leads.length) || 0;
  const newLeads = leads.filter(l => l.status === 'New').length;
  const qualifiedLeads = leads.filter(l => l.status === 'Qualified').length;
  const closedLeads = leads.filter(l => l.status === 'Closed').length;

  return (
    <div className="App">
      <header>
        <div className="header-content">
          <div className="logo-section">
            <h1>RealtorAssist</h1>
            <span className="subtitle">Lead Dashboard</span>
          </div>
        </div>
      </header>

      <div className="stats-grid">
        <div className="stat-box">
          <span className="stat-number">{leads.length}</span>
          <span className="stat-label">Total Leads</span>
        </div>
        <div className="stat-box hot">
          <span className="stat-number">{leads.filter(l => l.temperature === 'HOT').length}</span>
          <span className="stat-label">Hot Leads</span>
        </div>
        <div className="stat-box warm">
          <span className="stat-number">{leads.filter(l => l.temperature === 'WARM').length}</span>
          <span className="stat-label">Warm Leads</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{newLeads}</span>
          <span className="stat-label">New</span>
        </div>
        <div className="stat-box success">
          <span className="stat-number">{qualifiedLeads}</span>
          <span className="stat-label">Qualified</span>
        </div>
        <div className="stat-box success">
          <span className="stat-number">{closedLeads}</span>
          <span className="stat-label">Closed</span>
        </div>
        <div className="stat-box">
          <span className="stat-number">{avgScore}</span>
          <span className="stat-label">Avg Score</span>
        </div>
      </div>

      <div className="controls">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <div className="filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="status-select">
            <option value="all">All Status</option>
            <option value="New">New</option>
            <option value="Contacted">Contacted</option>
            <option value="Qualified">Qualified</option>
            <option value="Lost">Lost</option>
            <option value="Closed">Closed</option>
          </select>

          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={filter === 'hot' ? 'active hot' : 'hot'}
            onClick={() => setFilter('hot')}
          >
            Hot
          </button>
          <button 
            className={filter === 'warm' ? 'active warm' : 'warm'}
            onClick={() => setFilter('warm')}
          >
            Warm
          </button>
          <button 
            className={filter === 'cold' ? 'active cold' : 'cold'}
            onClick={() => setFilter('cold')}
          >
            Cold
          </button>
          
          <button onClick={exportToCSV} className="export-btn">
            �� Export CSV
          </button>
        </div>
      </div>

      <div className="leads-table">
        <table>
          <thead>
            <tr>
              <th onClick={() => sortLeads('temperature')} className="sortable">
                Temp {sortConfig.key === 'temperature' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => sortLeads('leadScore')} className="sortable">
                Score {sortConfig.key === 'leadScore' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => sortLeads('name')} className="sortable">
                Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Contact</th>
              <th>Budget</th>
              <th>Beds</th>
              <th>Timeline</th>
              <th onClick={() => sortLeads('status')} className="sortable">
                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => sortLeads('createdAt')} className="sortable">
                Date {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedLeads.map((lead, idx) => (
              <tr key={idx}>
                <td>
                  <span 
                    className="temp-badge"
                    style={{ backgroundColor: getTempColor(lead.temperature) }}
                  >
                    {lead.temperature}
                  </span>
                </td>
                <td>
                  <span className="score-badge">{lead.leadScore || 0}</span>
                </td>
                <td className="name-cell">{lead.name || '—'}</td>
                <td className="contact-cell">
                  <div>{lead.email || '—'}</div>
                  <div className="phone-sub">{lead.phone || ''}</div>
                </td>
                <td>{lead.budget || '—'}</td>
                <td>{lead.bedrooms || '—'}</td>
                <td>{lead.timeline || '—'}</td>
                <td>
                  <select 
                    value={lead.status} 
                    onChange={(e) => updateLeadStatus(lead, e.target.value)}
                    className="status-dropdown"
                  >
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Lost">Lost</option>
                    <option value="Closed">Closed</option>
                  </select>
                </td>
                <td className="date-cell">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <button 
                    className="view-btn"
                    onClick={() => viewLead(lead)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedLeads.length === 0 && (
        <div className="no-results">No leads match your filters.</div>
      )}

      {showModal && selectedLead && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedLead.name || 'Lead Details'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="lead-info">
                <div className="info-row">
                  <strong>Email:</strong> {selectedLead.email || '—'}
                </div>
                <div className="info-row">
                  <strong>Phone:</strong> {selectedLead.phone || '—'}
                </div>
                <div className="info-row">
                  <strong>Budget:</strong> {selectedLead.budget || '—'}
                </div>
                <div className="info-row">
                  <strong>Bedrooms:</strong> {selectedLead.bedrooms || '—'}
                </div>
                <div className="info-row">
                  <strong>Timeline:</strong> {selectedLead.timeline || '—'}
                </div>
                <div className="info-row">
                  <strong>Location:</strong> {selectedLead.location || '—'}
                </div>
                <div className="info-row">
                  <strong>Pre-Approved:</strong> {selectedLead.preApproval || '—'}
                </div>
                <div className="info-row">
                  <strong>Lead Score:</strong> <span className="score-badge">{selectedLead.leadScore || 0}/100</span>
                </div>
              </div>

              <div className="conversation-section">
                <h3>Conversation History</h3>
                {!selectedLead.leadId ? (
                  <p className="no-conversation">Lead ID missing - conversation not available</p>
                ) : selectedLead.conversation && selectedLead.conversation.length > 0 ? (
                  <div className="conversation">
                    {selectedLead.conversation.map((msg, idx) => (
                      <div key={idx} className={`message ${msg.role}`}>
                        <strong>{msg.role === 'user' ? 'Lead' : 'Assistant'}:</strong>
                        <p>{msg.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-conversation">No conversation history available</p>
                )}
              </div>

              <div className="notes-section">
                <h3>Notes</h3>
                <textarea
                  className="notes-textarea"
                  defaultValue={selectedLead.notes || ''}
                  placeholder="Add notes about this lead..."
                  onBlur={(e) => updateLeadNotes(selectedLead.leadId, e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
